import { formatAmount } from 'shared/helpers/numbers/amount';
import { LUXE_CATALOG_TAG } from 'shared/constants/storeConfig';
import { CartItem, CartItemTag } from 'shared/types/cart';
import { CART_ITEM_TAG_TYPE, TAG_ALT, TAG_IMAGES, TAG_TEXT_CONFIG, TEXTS } from '../constants';

type TagTextConfigKey = keyof typeof TAG_TEXT_CONFIG;

export type RevampCardTone = 'default' | 'sale' | 'luxe';

export type RevampDetailTagKind =
  | 'priceDrop'
  | 'urgency'
  | 'sale'
  | 'pinkFridaySale'
  | 'luxe'
  | 'freeGift'
  | 'nexpress'
  | 'peopleBrowsing'
  | 'sellingFast';

export interface RevampDetailTag {
  kind: RevampDetailTagKind;
  /** Optional dynamic copy shown next to the tag image (e.g. price-drop amount). */
  text: string | null;
  imageUrl?: string;
  alt: string;
  textConfigKey?: TagTextConfigKey;
  /** Refresh API itemTags[].type — used for remote-config styling lookup. */
  itemTagType?: string;
}

/** @deprecated Use RevampDetailTag — kept for existing test imports. */
export type RevampTopBannerKind = RevampDetailTagKind;
/** @deprecated Use RevampDetailTag */
export type RevampTopBanner = RevampDetailTag;

export interface RevampCardPresentation {
  cardTone: RevampCardTone;
  detailTag: RevampDetailTag | null;
  /** @deprecated Prefer detailTag */
  topBanner: RevampDetailTag | null;
  /** @deprecated Prefer detailTag */
  brandTag: RevampDetailTag | null;
}

interface GetCardPresentationParams {
  item: CartItem;
  currencySymbol: string;
  hidePriceDrop: boolean;
  showSaleBanner: boolean;
  saleTagText: string;
  luxeTagText: string;
}

export function isLuxeItem(item: CartItem): boolean {
  const store = item.trackingParam?.store;
  if (store === LUXE_CATALOG_TAG) {
    return true;
  }

  return Boolean(item.catalogTag?.includes(LUXE_CATALOG_TAG));
}

function isFreeGiftItem(item: CartItem): boolean {
  return Boolean(item.cartItemFlagDTO?.isFreeProduct || item.cartItemFlagDTO?.isFreeSample);
}

function isNexpressItem(item: CartItem): boolean {
  const edd = item.eddMessage || '';
  return /express/i.test(edd);
}

function getUrgencyDetailTag(urgencyTag: string): RevampDetailTag {
  if (/browsing|looking|viewing|people/i.test(urgencyTag)) {
    return {
      kind: 'peopleBrowsing',
      text: urgencyTag,
      textConfigKey: 'peopleBrowsing',
      alt: urgencyTag || TAG_ALT.peopleBrowsing,
    };
  }

  return {
    kind: 'sellingFast',
    text: urgencyTag || TEXTS.DEFAULT_URGENCY_TAG,
    textConfigKey: urgencyTag !== TEXTS.DEFAULT_URGENCY_TAG ? 'sellingFastColored' : 'sellingFast',
    alt: urgencyTag || TAG_ALT.sellingFast,
  };
}

/** Backend-sorted list — first entry is the winning tag from API. */
export function getDisplayItemTag(itemTags?: CartItemTag[]): CartItemTag | null {
  return itemTags?.[0] || null;
}

function isLuxeTagType(type?: string): boolean {
  return type?.toUpperCase() === CART_ITEM_TAG_TYPE.LUXE;
}

/** Luxe catalog items show the Luxe badge unless the first sorted tag is Sale. */
export function shouldShowLuxeCatalogTag(item: CartItem): boolean {
  const firstTag = getDisplayItemTag(item.itemTags);
  return isLuxeItem(item) && firstTag?.type !== CART_ITEM_TAG_TYPE.ON_SALE;
}

/**
 * Tag type for revamp card + cart analytics.
 * Uses itemTags[0]; Luxe products report LUXE when the catalog Luxe badge is shown.
 */
export function getResolvedProductTagType(
  item: Pick<CartItem, 'itemTags' | 'trackingParam' | 'catalogTag'>
): string | null {
  const firstTag = getDisplayItemTag(item.itemTags);

  if (shouldShowLuxeCatalogTag(item as CartItem)) {
    if (firstTag && isLuxeTagType(firstTag.type)) {
      return firstTag.type;
    }
    return CART_ITEM_TAG_TYPE.LUXE;
  }

  return firstTag?.type ?? null;
}

function mapItemTagToDetailTag(tag: CartItemTag): RevampDetailTag {
  const text = tag.displayText;
  const itemTagType = tag.type;

  switch (tag.type) {
    case CART_ITEM_TAG_TYPE.ON_SALE:
      return {
        kind: 'sale',
        text,
        alt: text || TAG_ALT.sale,
        textConfigKey: 'sale',
        itemTagType,
      };
    case CART_ITEM_TAG_TYPE.PRICE_DROP:
      return {
        kind: 'priceDrop',
        text,
        alt: text || TAG_ALT.priceDrop,
        itemTagType,
      };
    case CART_ITEM_TAG_TYPE.SELLING_FAST:
      return {
        kind: 'sellingFast',
        text,
        textConfigKey: 'sellingFastColored',
        alt: text || TAG_ALT.sellingFast,
        itemTagType,
      };
    case CART_ITEM_TAG_TYPE.LUXE:
      return {
        kind: 'luxe',
        text,
        alt: text || TAG_ALT.luxe,
        textConfigKey: 'luxe',
        itemTagType,
      };
    case CART_ITEM_TAG_TYPE.THIRTY_DAY_BEST_PRICE:
    case CART_ITEM_TAG_TYPE.TRENDING_NOW:
    case CART_ITEM_TAG_TYPE.TOP_RATED:
      return {
        kind: 'sellingFast',
        text,
        alt: text,
        itemTagType,
      };
    default:
      return {
        kind: 'sellingFast',
        text,
        textConfigKey: 'sellingFast',
        alt: text,
        itemTagType,
      };
  }
}

export function getCardPresentation({
  item,
  currencySymbol,
  hidePriceDrop,
  showSaleBanner,
  saleTagText,
  luxeTagText,
}: GetCardPresentationParams): RevampCardPresentation {
  const withLegacyAliases = (
    cardTone: RevampCardTone,
    detailTag: RevampDetailTag | null,
    asBrandTag = false
  ): RevampCardPresentation => ({
    cardTone,
    detailTag,
    topBanner: asBrandTag ? null : detailTag,
    brandTag: asBrandTag ? detailTag : null,
  });

  if (isFreeGiftItem(item)) {
    return withLegacyAliases('default', {
      kind: 'freeGift',
      text: TAG_ALT.freeGift,
      alt: TAG_ALT.freeGift,
    });
  }

  const primaryItemTag = getDisplayItemTag(item.itemTags);

  if (shouldShowLuxeCatalogTag(item)) {
    return withLegacyAliases(
      'luxe',
      {
        kind: 'luxe',
        text: luxeTagText || TEXTS.DEFAULT_LUXE_TAG,
        alt: luxeTagText || TAG_ALT.luxe,
      },
      true
    );
  }

  if (primaryItemTag) {
    const detailTag = mapItemTagToDetailTag(primaryItemTag);
    const cardTone: RevampCardTone =
      primaryItemTag.type === CART_ITEM_TAG_TYPE.ON_SALE
        ? 'sale'
        : primaryItemTag.type === CART_ITEM_TAG_TYPE.LUXE
          ? 'luxe'
          : 'default';

    return withLegacyAliases(
      cardTone,
      detailTag,
      cardTone === 'luxe' && detailTag.kind === 'luxe'
    );
  }

  if (isNexpressItem(item)) {
    return withLegacyAliases('default', {
      kind: 'nexpress',
      text: null,
      imageUrl: TAG_IMAGES.nexpress,
      alt: TAG_ALT.nexpress,
    });
  }

  if (showSaleBanner && item.onSale) {
    const isPinkFridaySale = /pink friday/i.test(saleTagText || '');

    return withLegacyAliases('sale', {
      kind: isPinkFridaySale ? 'pinkFridaySale' : 'sale',
      text: null,
      imageUrl: isPinkFridaySale ? TAG_IMAGES.pinkFridaySale : TAG_IMAGES.sale,
      alt: isPinkFridaySale ? TAG_ALT.pinkFridaySale : TAG_ALT.sale,
    });
  }

  if (item.urgencyTag) {
    return withLegacyAliases('default', getUrgencyDetailTag(item.urgencyTag));
  }

  if (!hidePriceDrop && item.priceDropValue) {
    return withLegacyAliases('default', {
      kind: 'priceDrop',
      text: `${TAG_ALT.priceDrop} ${TEXTS.PRICE_DROPPED_AMOUNT_PREFIX
        }${currencySymbol}${formatAmount(item.priceDropValue)}`,
      alt: TAG_ALT.priceDrop,
    });
  }

  return withLegacyAliases('default', null);
}
